import { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        setCurrentUser(user);
        
        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() };
            
            // Only check status if email is not verified
            if (userData.status === 'inactive' && !user.emailVerified) {
              console.log('User is inactive and email not verified, signing out...');
              await auth.signOut();
              setCurrentUser(null);
              setUserProfile(null);
              setLoading(false);
              return;
            }
            
            // If email is verified but status is still inactive, update it
            if (userData.status === 'inactive' && user.emailVerified) {
              console.log('Email verified but status is inactive, updating status...');
              try {
                await updateDoc(doc(db, 'users', user.uid), {
                  status: 'active',
                  updatedAt: new Date().toISOString()
                });
                userData.status = 'active';
              } catch (updateError) {
                console.error('Error updating user status:', updateError);
              }
            }
            
            setUserProfile(userData);
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setUserProfile(null);
        }
      } else {
        // User is signed out
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    userProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
